/**
 * Floating Add Button with Callout Options
 *
 * A floating action button that shows callout buttons for:
 * - Adding a comment
 * - Adding a trade
 *
 * Features:
 * - Fixed bottom-right positioning
 * - Animated callout buttons
 * - Theme-aware styling
 */

import React, { useMemo, useState, useRef, useEffect } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Animated,
  GestureResponderEvent,
} from 'react-native';
import { useTheme } from '../../contexts/ThemeContext';
import { Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';

interface CalloutOption {
  id: string;
  icon: string;
  label: string;
  onPress: (event: GestureResponderEvent) => void;
}

interface FloatingAddButtonProps {
  onAddComment: (event: GestureResponderEvent) => void;
  onAddTrade: (symbol: string, currency: string) => void;
  onAddCorporateAction?: (symbol: string) => void; // Optional: for position detail screen
  symbol: string;
  currency: string;
  testID?: string;
}

export function FloatingAddButton({
  onAddComment,
  onAddTrade,
  onAddCorporateAction,
  symbol,
  currency,
  testID,
}: FloatingAddButtonProps) {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);

  const [isCalloutVisible, setIsCalloutVisible] = useState(false);
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const slideAnim = useRef(new Animated.Value(0)).current;

  // Scale animation for main button
  const scaleAnim = useRef(new Animated.Value(1)).current;

  const toggleCallout = () => {
    setIsCalloutVisible((prev) => !prev);
  };

  // Animate callout visibility
  useEffect(() => {
    if (isCalloutVisible) {
      Animated.parallel([
        Animated.timing(fadeAnim, {
          toValue: 1,
          duration: 200,
          useNativeDriver: true,
        }),
        Animated.timing(slideAnim, {
          toValue: 1,
          duration: 200,
          useNativeDriver: true,
        }),
      ]).start();
    } else {
      Animated.parallel([
        Animated.timing(fadeAnim, {
          toValue: 0,
          duration: 150,
          useNativeDriver: true,
        }),
        Animated.timing(slideAnim, {
          toValue: 0,
          duration: 150,
          useNativeDriver: true,
        }),
      ]).start();
    }
  }, [isCalloutVisible, fadeAnim, slideAnim]);

  const handleMainButtonPress = () => {
    toggleCallout();
  };

  const handleBackdropPress = () => {
    setIsCalloutVisible(false);
  };

  const handleCalloutOption = (callback: (event: GestureResponderEvent) => void) => {
    setIsCalloutVisible(false);
    return callback;
  };

  const handleAddComment = (event: GestureResponderEvent) => {
    handleCalloutOption(onAddComment)(event);
  };

  const handleAddTrade = () => {
    setIsCalloutVisible(false);
    onAddTrade(symbol, currency);
  };

  const handleAddCorporateAction = () => {
    setIsCalloutVisible(false);
    if (onAddCorporateAction) {
      onAddCorporateAction(symbol);
    }
  };

  // Build callout options array
  const calloutOptions: CalloutOption[] = [
    {
      id: 'comment',
      icon: 'chatbubble-ellipses',
      label: t('portfolio.positionComment.addComment', { defaultValue: 'Add Comment' }),
      onPress: handleAddComment,
    },
    {
      id: 'trade',
      icon: 'add-circle',
      label: t('portfolio.addTrade', { defaultValue: 'Add Trade' }),
      onPress: handleAddTrade,
    },
  ];

  // Add corporate action option if the callback is provided
  if (onAddCorporateAction) {
    calloutOptions.push({
      id: 'corporate-action',
      icon: 'swap-horizontal',
      label: t('portfolio.addCorporateAction', { defaultValue: 'Add Corporate Action' }),
      onPress: handleAddCorporateAction,
    });
  }

  return (
    <View style={styles.container}>
      {isCalloutVisible && (
        <TouchableOpacity style={styles.backdrop} onPress={handleBackdropPress} activeOpacity={1} />
      )}
      {isCalloutVisible && (
        <Animated.View
          style={[
            styles.calloutContainer,
            {
              opacity: fadeAnim,
              transform: [
                { translateY: slideAnim.interpolate({ inputRange: [0, 1], outputRange: [20, 0] }) },
              ],
            },
          ]}
        >
          {calloutOptions.map((option) => (
            <Animated.View
              key={option.id}
              style={[
                styles.calloutItem,
                {
                  opacity: fadeAnim,
                  transform: [
                    { scale: slideAnim.interpolate({ inputRange: [0, 1], outputRange: [0.5, 1] }) },
                  ],
                },
              ]}
            >
              <TouchableOpacity
                style={[
                  styles.calloutButton,
                  {
                    backgroundColor: theme.colors.surface,
                    borderColor: theme.colors.border,
                    flexDirection: 'row',
                  },
                ]}
                onPress={option.onPress}
                activeOpacity={0.7}
              >
                <Ionicons
                  name={option.icon as any}
                  size={20}
                  color={theme.colors.primary}
                  style={styles.calloutIcon}
                />
                <Text style={[styles.calloutLabel, { color: theme.colors.text }]}>
                  {option.label}
                </Text>
              </TouchableOpacity>
            </Animated.View>
          ))}
        </Animated.View>
      )}
      <TouchableOpacity
        testID={testID}
        onPress={handleMainButtonPress}
        activeOpacity={0.7}
        style={[
          styles.mainButton,
          { backgroundColor: theme.colors.primary, transform: [{ scale: scaleAnim }] },
        ]}
      >
        <Ionicons name="add" size={28} color={theme.colors.textInverse} />
      </TouchableOpacity>
      {theme.id === 'bauhaus' && (
        <View style={[styles.bauhausShadow, { backgroundColor: theme.colors.text }]} />
      )}
    </View>
  );
}

function createThemedStyles(theme: any) {
  return StyleSheet.create({
    container: {
      position: 'absolute',
      bottom: 24,
      right: 16,
      zIndex: 1000,
      alignItems: 'flex-end',
    },
    backdrop: {
      position: 'absolute',
      top: -1000,
      left: -1000,
      right: -1000,
      bottom: -1000,
      backgroundColor: 'transparent',
      zIndex: -1,
    },
    mainButton: {
      width: 56,
      height: 56,
      borderRadius: theme.id === 'bauhaus' ? 16 : 28,
      justifyContent: 'center',
      alignItems: 'center',
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.25,
      shadowRadius: 4,
      elevation: 4,
    },
    calloutContainer: {
      position: 'absolute',
      bottom: 70,
      alignItems: 'flex-end',
      gap: 12,
    },
    calloutItem: {
      alignItems: 'flex-end',
    },
    calloutButton: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: 8,
      paddingVertical: 8,
      paddingHorizontal: 12,
      borderRadius: theme.radii.lg,
      borderWidth: 1,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.1,
      shadowRadius: 2,
      elevation: 2,
      minWidth: 120,
    },
    calloutLabel: {
      fontSize: theme.typography.fontSizeXs,
      fontWeight: theme.typography.fontWeightMedium,
      flex: 1,
    },
    calloutIcon: {
      flexShrink: 0,
      marginTop: 2,
    },
    bauhausShadow: {
      position: 'absolute',
      bottom: -4,
      right: -4,
      width: 56,
      height: 56,
      borderRadius: 16,
      zIndex: -1,
    },
  });
}
